/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useServiceContext } from '../../root'
import { RcmTableDataWrapper } from '../../components/rcm-table/RcmTableDataWrapper'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import { DocumentsTable } from './DocumentsTable'
import { DocumentsTableFilter } from './DocumentsTableFilter'
import { SystemFolderType } from '../../constants/fsi'
import useDocumentsListService from '../../hooks/useDocumentsListService'
import { DOCUMENTS_VIEW_TYPE, DocumentsViewType, PageConfiguration, FolderDetails } from './types'
import { PageMenu } from './PageMenu'
import { PropertySearchPageMenu } from './PropertySearchPageMenu'
import { SearchBar } from './SearchBar'
import { Breadcrumbs } from '../../components/doc-preview/Breadcrumbs'
import { GridTableSwitch } from './GridTableSwitch'
import { ReportsTable } from './ReportsTable'
import { ReportsTableFilter } from './ReportsTableFilter'
import { PeriodicReviewTable } from './PeriodicReviewTable'
import RcmTableFilterTrigger from '../../components/rcm-table/RcmTableFilterTrigger'
import usePropertySearchService from '~/hooks/usePropertySearchService'
import PropertySearchFilter from '../../propertySearch/property-search/PropertySearchFilter'
import WorkflowDetailsModal from '~/components/modals/WorkflowDetailsModal'
import { useHelpPage } from '../../services/helpPage'
import { emitCustomCheckmateAction } from '../../services/checkmate'

interface DocumentsListPageProps {
  viewType: DocumentsViewType
  folderId?: string | number
  initialPage?: number
  urlFilterId?: string
  isCommonFav?: boolean
}

export const DocumentsListPage: React.FC<DocumentsListPageProps> = ({
  viewType,
  folderId,
  initialPage = 1,
  urlFilterId,
  isCommonFav
}) => {
  const navigate = useNavigate()
  const context = useServiceContext()
  const {
    service: { auth },
    api
  } = context
  const {
    loadRecent,
    loadFavorites,
    loadPeriodicReview,
    loadReports,
    loadRegular,
    getSystemDocumentsDefinition,
    getReportFolderDefinition
  } = useDocumentsListService()

  const isRegularFolder = [
    SystemFolderType.FORMS,
    SystemFolderType.TEMPLATES,
    SystemFolderType.INIT_ETMF_FILES,
    SystemFolderType.REGULATED_CONTENT
  ].includes(viewType as any)

  const showFilterAndBreadcrumbs = isRegularFolder || viewType !== DOCUMENTS_VIEW_TYPE.systemDocuments

  const isReportPage = [
    SystemFolderType.EMAIL_UPLOADS,
    SystemFolderType.REPORTS,
    SystemFolderType.DOCUMENT_MIGRATIONS_AND_EXPORTS,
    SystemFolderType.SETTINGS_AND_SYSTEM_EXPORTS_IMPORTS
  ].includes(viewType as any)

  const persistenceKey = `browser_${folderId || 'abstract'}_${viewType}`
  const [folderDetails, setFolderDetails] = useState<FolderDetails>({})
  const [hideVersion, setHideVersion] = useState<any>(false)
  const { loadData: propertySearchLoadData } = usePropertySearchService()
  const { setStateName } = useHelpPage()

  // Store service functions in refs to avoid useMemo recalculation
  const serviceFunctionsRef = useRef({
    loadFavorites,
    loadRecent,
    loadPeriodicReview,
    loadRegular,
    getSystemDocumentsDefinition,
    loadReports,
    propertySearchLoadData,
    getReportFolderDefinition
  })

  // Update refs when functions change (but don't trigger re-renders)
  useEffect(() => {
    serviceFunctionsRef.current = {
      loadFavorites,
      loadRecent,
      loadPeriodicReview,
      loadRegular,
      getSystemDocumentsDefinition,
      loadReports,
      propertySearchLoadData,
      getReportFolderDefinition
    }
  })

  // Compute configuration synchronously during render - this ensures it's ready when RcmTableDataWrapper mounts
  const configuration = useMemo<PageConfiguration>(() => {
    const funcs = serviceFunctionsRef.current

    switch (viewType) {
      case DOCUMENTS_VIEW_TYPE.propertySearch:
        return {
          title: 'BROWSER_VIEW_PROPERTY_FILTER',
          showActionMenu: true,
          showFilter: true,
          showBreadcrumbs: false,
          updatePageUrlParam: false,
          isAbstract: true,
          isPropertySearch: true,
          showGridViewTrigger: true,
          loaded: true,
          loadDataFn: funcs.propertySearchLoadData
        }
      case DOCUMENTS_VIEW_TYPE.favorites:
        return {
          title: 'FAVORITES',
          showActionMenu: false,
          showFilter: false,
          showBreadcrumbs: false,
          updatePageUrlParam: true,
          isAbstract: true,
          showGridViewTrigger: true,
          loaded: true,
          loadDataFn: funcs.loadFavorites
        }
      case DOCUMENTS_VIEW_TYPE.recent:
        return {
          title: 'RECENT_DOCUMENTS',
          showActionMenu: false,
          showFilter: false,
          updatePageUrlParam: true,
          showBreadcrumbs: false,
          isAbstract: true,
          showGridViewTrigger: true,
          loaded: true,
          loadDataFn: funcs.loadRecent
        }
      case DOCUMENTS_VIEW_TYPE.periodicReviewUpcoming:
        return {
          title: 'PERIODIC_REVIEW',
          showActionMenu: false,
          showFilter: false,
          updatePageUrlParam: true,
          showBreadcrumbs: false,
          isAbstract: true,
          isPeriodicReviewView: true,
          showGridViewTrigger: true,
          loaded: true,
          loadDataFn: funcs.loadPeriodicReview
        }
      case DOCUMENTS_VIEW_TYPE.regulatedContent:
        return {
          title: 'BROWSER_DOCUMENTS_TITLE',
          showActionMenu: true,
          showFilter: true,
          showBreadcrumbs: true,
          updatePageUrlParam: true,
          isAbstract: false,
          showGridViewTrigger: true,
          loaded: true,
          loadDataFn: (queryParams, filter) => {
            return funcs.loadRegular(Number(folderId), queryParams, filter)
          }
        }
      default:
        return {
          title: 'BROWSER_VIEW_SYSTEM_DOCUMENTS',
          showActionMenu: isRegularFolder ? true : false,
          showGridViewTrigger: isRegularFolder ? true : false,
          updatePageUrlParam: isRegularFolder ? true : false,
          showFilter: showFilterAndBreadcrumbs ? true : false,
          showBreadcrumbs: showFilterAndBreadcrumbs ? true : false,
          isAbstract: isRegularFolder ? false : true,
          isReportView: !isRegularFolder && viewType !== DOCUMENTS_VIEW_TYPE.systemDocuments ? true : false,
          loaded: true,
          loadDataFn: (queryParams, filter) => {
            if (viewType === DOCUMENTS_VIEW_TYPE.systemDocuments) {
              return funcs.getSystemDocumentsDefinition(queryParams, auth.getRole() || '')
            } else if (isRegularFolder) {
              return funcs.loadRegular(Number(folderId), queryParams, filter)
            } else {
              return funcs.loadReports(queryParams, filter)
            }
          }
        }
    }
  }, [viewType, folderId, isRegularFolder, showFilterAndBreadcrumbs, auth])

  // Use ref to always access latest configuration without causing re-renders
  const configurationRef = useRef(configuration)
  // Update ref on every render (no dependencies) - this doesn't cause re-renders
  configurationRef.current = configuration

  const downloadAndSetFolderDetails = useCallback(() => {
    if (!parseInt(String(folderId || '0'))) {
      if (isReportPage) {
        const reportFolderDef = serviceFunctionsRef.current.getReportFolderDefinition(viewType as any)
        // For report views, show the report folder as breadcrumb (no System Documents parent)
        setFolderDetails({
          ...reportFolderDef,
          path: [],
          // Set the report folder itself as the current page (will show as active breadcrumb)
          showCurrentAsLink: true
        })
      } else {
        setFolderDetails({
          path: []
        })
      }
      return
    }

    // Load folder details from API
    const promises = [api.files.getFileDetail(Number(folderId)), api.files.getFilePath(Number(folderId))]

    Promise.all(promises)
      .then((data) => {
        const details = data[0] as any
        const fullPath = data[1] as any[]

        // Legacy pattern: pop() the last path item (the current folder's own entry from the
        // path endpoint) and MERGE it into the folder details — this is how mccObjType and
        // mccObjUuid end up on vm.currentFolder in the legacy AngularJS code:
        //   var lastPathItem = path.pop()
        //   vm.currentFolder = _.extend(data[1].plain(), lastPathItem)
        // Without this merge, emitCustomCheckmateAction can't find study/env/site folders
        // in the path (the API returns mccObjType only in the path endpoint, not /detail).
        const pathItems = [...fullPath]
        const lastPathItem = pathItems.pop() // Current folder entry with mccObjType/mccObjUuid

        setFolderDetails({
          ...details,
          // Merge MCC fields (mccObjType, mccObjUuid, etc.) from the path endpoint's
          // last item into folderDetails — matching legacy _.extend(detail, lastPathItem)
          ...(lastPathItem || {}),
          // Re-apply critical fields from details so they always take precedence over
          // the path item's minimal representation. The path endpoint only has id/name/acl/
          // aclLevel/mccObjType/mccObjUuid — but details has the full rich object including
          // actionFlags, systemFolderType, uuid, fsiTypeTemplate, etc. which must not be lost.
          id: details.id,
          name: details.name,
          acl: details.acl,
          actionFlags: details.actionFlags,
          systemFolderType: details.systemFolderType,
          uuid: details.uuid,
          path: pathItems,
          folderPathText:
            pathItems.reduce((memo: string, item: any) => {
              return (memo ? memo + '/' : '') + item.name
            }, '') +
            '/' +
            details.name
        })
      })
      .catch((error) => {
        console.error('Failed to load folder details:', error)
        setFolderDetails({
          id: Number(folderId),
          path: []
        })
      })
  }, [folderId, isReportPage, viewType, api.files])

  useEffect(() => {
    downloadAndSetFolderDetails()
    const handler = () => downloadAndSetFolderDetails()
    window.addEventListener('updateFiles', handler)
    return () => window.removeEventListener('updateFiles', handler)
  }, [folderId, downloadAndSetFolderDetails])

  // Set help page state and emit checkmate action based on viewType
  useEffect(() => {
    let stateName = 'browser'
    //@ts-expect-error -- IGNORE --
    if (viewType === SystemFolderType.TEMPLATES) {
      stateName = 'browser.templates'
    } else if (viewType === SystemFolderType.SYSTEM_DOCUMENTS) {
      stateName = 'browser.systemDocuments'
      //@ts-expect-error -- IGNORE --
    } else if (viewType === SystemFolderType.FORMS) {
      stateName = 'browser.forms'
    } else if (viewType === DOCUMENTS_VIEW_TYPE.propertySearch) {
      stateName = 'reports.propertySearch'
    } else if (viewType === DOCUMENTS_VIEW_TYPE.periodicReviewUpcoming) {
      stateName = 'browser.periodicReviewUpcoming'
    } else if (viewType === DOCUMENTS_VIEW_TYPE.recent) {
      stateName = 'browser.recentDocuments'
    }
    setStateName(stateName)
    if (Object.keys(folderDetails || {}).length > 0) {
      emitCustomCheckmateAction(folderDetails, stateName)
    }
  }, [folderDetails, viewType, setStateName])

  useEffect(() => {
    setHideVersion(context.service.auth.getPolicies?.()?.hideStackedDocumentVersion)
    return () => {
      if (DOCUMENTS_VIEW_TYPE.regulatedContent !== viewType) {
        window.localStorage.removeItem('allFoldersAndDocumentsFilterCollapsed')
      }
    }
  }, [viewType])

  const loadData = useCallback((queryParams: any, filter: any) => {
    const promise = new Promise<{ elements: any[]; hasNextPage: boolean; totalElements: number }>((resolve, reject) => {
      // Access latest configuration via ref
      const currentConfig = configurationRef.current

      // Don't attempt to load if configuration isn't ready yet
      if (!currentConfig.loaded || !currentConfig.loadDataFn) {
        reject(new Error('No load function configured'))
        return
      }

      currentConfig.loadDataFn(queryParams, filter).then(
        (data) => {
          resolve({
            elements: data.elements,
            hasNextPage: data.hasNextPage || false,
            totalElements: data.totalElements
          })
        },
        (error) => {
          reject(error)
        }
      )
    })

    return promise
  }, [])

  // Navigation handler for folder/document clicks
  const handleNavigateToFolder = useCallback(
    (folderId: number) => {
      navigate(`/browser/${folderId}`)
    },
    [navigate]
  )

  return (
    <div className="mcc-content-frame theme-sandman">
      <RcmTableDataWrapper
        loadData={loadData}
        defaultSortBy={'NAME'}
        defaultSortDesc={false}
        initialPage={initialPage}
        updateUrlPageParam={configuration.updatePageUrlParam}
        persistenceKey={persistenceKey}
        doPreload={configuration.loaded && !configuration.showFilter}
      >
        <DocumentsListPageContent
          configuration={configuration}
          folderDetails={folderDetails}
          viewType={viewType}
          initialPage={initialPage}
          hideVersion={hideVersion}
          onNavigateToFolder={handleNavigateToFolder}
          urlFilterId={urlFilterId}
          isCommonFav={isCommonFav}
        />
      </RcmTableDataWrapper>
    </div>
  )
}

// Inner component that has access to RcmTableDataContext
const DocumentsListPageContent: React.FC<{
  configuration: PageConfiguration
  folderDetails: FolderDetails
  viewType: DocumentsViewType
  initialPage: number
  hideVersion: boolean
  onNavigateToFolder: (folderId: number) => void
  urlFilterId?: string
  isCommonFav?: boolean
}> = ({
  configuration,
  folderDetails,
  viewType,
  initialPage,
  hideVersion,
  onNavigateToFolder,
  urlFilterId,
  isCommonFav
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tableData = useRcmTableData()
  const [workFlowModalDetails, setWorkFlowModalDetails] = useState({
    isModalVisible: false,
    id: null as number | null
  })

  return (
    <div className="page-frame">
      <div className={configuration.isAbstract ? 'mb10' : ''}>
        <div className="row" style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <div className="col-xs-6" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h1 id="2d849193-cb05-48df-a4a1-23eb0dc678ef" style={{ margin: 0, whiteSpace: 'nowrap' }}>
              {translate(configuration.title)}
            </h1>
            {!configuration.isPropertySearch && !isCommonFav && <SearchBar />}
          </div>
          <div
            className="col-xs-6"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px' }}
          >
            {configuration.isPropertySearch ? (
              <PropertySearchPageMenu />
            ) : (
              configuration.showActionMenu &&
              folderDetails?.id && <PageMenu fsi={folderDetails} options={{ hideView: true }} />
            )}
            {configuration.showFilter && <RcmTableFilterTrigger />}
            {configuration.showGridViewTrigger && <GridTableSwitch />}
          </div>
        </div>
        {configuration.showBreadcrumbs && <Breadcrumbs fsi={folderDetails as any} />}
      </div>

      <div className="page-content">
        {configuration.isPropertySearch ? (
          <PropertySearchFilter filterId={urlFilterId} />
        ) : configuration.isReportView ? (
          <>
            {configuration.showFilter && (
              <ReportsTableFilter viewType={viewType} isFilterCollapsed={tableData.isFilterCollapsed} />
            )}
          </>
        ) : (
          <>
            {configuration.showFilter && folderDetails?.id && (
              <DocumentsTableFilter
                fsi={folderDetails}
                isFilterCollapsed={tableData.isFilterCollapsed}
                configuration={configuration}
              />
            )}
          </>
        )}
        {configuration.loaded && (
          <>
            {configuration.isReportView ? (
              <ReportsTable viewType={viewType} fsi={folderDetails} />
            ) : configuration.isPeriodicReviewView ? (
              <PeriodicReviewTable
                viewType={viewType}
                initialPage={initialPage}
                filterShown={configuration.showFilter}
              />
            ) : (
              <DocumentsTable
                viewType={viewType}
                initialPage={initialPage}
                filterShown={configuration.showFilter}
                isPropertySearch={configuration.isPropertySearch}
                fsi={folderDetails}
                hideVersion={hideVersion && configuration?.title !== 'BROWSER_VIEW_SYSTEM_DOCUMENTS'}
                onNavigateToFolder={onNavigateToFolder}
                setWorkFlowModalDetails={setWorkFlowModalDetails}
                isCommonFav={isCommonFav}
              />
            )}
          </>
        )}
      </div>
      {workFlowModalDetails?.id && workFlowModalDetails?.isModalVisible && (
        <WorkflowDetailsModal
          wfId={workFlowModalDetails?.id}
          onClose={() =>
            setWorkFlowModalDetails({
              isModalVisible: false,
              id: null
            })
          }
        />
      )}
    </div>
  )
}
